/* eslint-disable camelcase */
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Appearance, ScrollView } from "react-native"
import { CompositeScreenProps } from "@react-navigation/native"
import { AutoFeederPondDailyFeedCreateMultipleForm as ModelForm, ModelCreateMultipleScreen, Text, JalaIcon } from "../../../../components"
import { observer } from "mobx-react-lite"
import { useStores } from "../../../../models"
import { AutoFeederPond, Feed } from "../../../../database"
import { goBack } from "../../../../navigators"
import { View } from "native-base"
import { PondStoreModel } from "../../../../models/app-store/farm-store/pond-store/pond-store"
import { color } from "../../../../theme"
import SyncableModel from "../../../../database/SyncableModel"
import { flatten, groupBy, keys, map, orderBy, sortBy, sumBy } from "lodash"
import moment from "moment"

export const AutoFeederPondDailyFeedCreateMultipleScreen: FC<{ colorScheme?: "light" | "dark" } & CompositeScreenProps<any, any>> = observer(function AutoFeederPondDailyFeedCreateMultipleScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), route: { params = {} } } = props

  // Pull in one of our MST stores
  const {
    appStore: { farmStore, farmStore: { pondStore }, farmId },
    feedStore: modelStore,
    autoFeederPondStore
  } = useStores();
  const { cycleId, cycleStore, autoFeederPonds = [] } = pondStore || PondStoreModel.create({})
  const autoFeederPond = autoFeederPonds.find((a) => `${a.id}` === `${params?.auto_feeder_pond_id}`)

  const [ models, setModels ] = useState<SyncableModel[]>([])
  const scrollViewRef = useRef<ScrollView>(null)

  const initialValues = useMemo<any>(() => {
    const data = (models.length ? models.map((m) => m.toArray()) : [])
    const feeds = map(groupBy(data, (d) => d.logged_at), (value, key) => {
      return {
        logged_at: key,
        logged_time: moment.utc(key).format('HH:mm:ss'),
        quantity: sumBy(value, 'quantity'),
        ids: value.map((v) => v.id),
        get quantities_ratio() {
          return value.map((v) => v.quantity / this.quantity);
        }
      }
    })

    let ms = []
    if (autoFeederPond) {
      if (autoFeederPond.state?.mode === 'continuous') {
        ms[0] = {
          logged_at: moment(autoFeederPond.state?.startTime, 'HH:mm').utc().format('YYYY-MM-DD HH:mm:ss'),
          get logged_time() {
            return moment.utc(this.logged_at).format('HH:mm:ss')
          },
          quantity: sumBy(feeds, 'quantity'),
          ids: flatten(feeds.map((f) => f.ids)),
          get quantities_ratio() {
            return flatten(feeds.map((v) => {
              const f = v.quantity / this.quantity
              return v.quantities_ratio.map((q) => f * q)
            }));
          }
        }
      } else {
        for (let i = 0; i < autoFeederPond.state.timesPerDay; i++) {
          ms[i] = feeds[i] || {}
        }
      }
    } else {
      ms = feeds
    }

    let initialVal = {
      farm_id: farmId,
      cycle_id: cycleId,
      feed_at: params.feed_at,
      fasting: !!data.find((d) => d.fasting) || false,
      models: ms,
      autoFeeders: []
    }

    if (autoFeederPonds.length > 1) {
      const autoFeeders = [];
      for (let i = 0; i < autoFeederPonds.length; i++) {
        autoFeeders.push({ feedRatio: '', name: autoFeederPonds[i].name });
      }
      initialVal = { ...initialVal, autoFeeders }
    }

    return initialVal
  }, [autoFeederPond, autoFeederPonds, cycleId, farmId, models, params.feed_at])

  useEffect(() => {
    if (cycleStore.loadingFeed) return
    const feeds = cycleStore.feeds ? cycleStore.feeds.filter((m) => m.feed_at === params.feed_at) : [];
    if (feeds.length) {
      const models = orderBy(feeds, 'logged_at').map((f) => Feed.hydrate({ ...f }))
      setModels(models)
    }
  }, [cycleStore.feeds, cycleStore.loadingFeed, farmStore, params.feed_at, setModels])

  useEffect(() => {
    cycleStore.getFeeds(false).then(() => {
      cycleStore.getFeeds(true).catch()
    });
  }, [cycleStore])

  const onSuccess = useCallback((success: boolean) => {
    if (success && params.auto_feeder_pond_id && autoFeederPond) {
      const updateAutoFeederPondFeeds = async () => {
        const model = AutoFeederPond.hydrate({ ...autoFeederPond })
        const feeds = model.state?.feeds || []
        const feed = feeds.find((f: any) => f.feed_at === params.feed_at)
        const feedModels = await Feed.fetch({ feed_at__eq: params.feed_at, cycle_id__eq: cycleId })
        const quantity = feedModels.length ? sumBy(feedModels, 'quantity') : null
        const feedsPerDayGroup = groupBy(feedModels, (f) => moment.utc(f.logged_at).local().format('HH:mm'));
        const times = keys(feedsPerDayGroup)
        const feedsPerDay = []
        for (let i = 0; i < model.state?.timesPerDay; i++) {
          feedsPerDay[i] = {
            time: times[i] || null,
            percent: (100 * sumBy((feedsPerDayGroup[times[i]] || []), 'quantity') / quantity)
          }
        }
        if (feed) {
          feed.quantity = quantity
          feed.feedsPerDay = sortBy(feedsPerDay, 'time')
        } else {
          feeds.push({
            feed_at: params.feed_at,
            quantity: quantity,
            feedsPerDay: sortBy(feedsPerDay, 'time')
          })
        }

        return autoFeederPondStore.updateOffline(model, {
          state: {
            ...model.state,
            updatedAt: moment().utc().format('YYYY-MM-DD HH:mm:ss'),
            feeds: sortBy(feeds, 'feed_at'),
            feedsPerDay: sortBy(feedsPerDay, 'time')
          }
        })
      }
      setTimeout(() => modelStore.set('loading', true), 1)
      updateAutoFeederPondFeeds()
        .then(() => {
          cycleStore.getFeeds(true).catch()
          return goBack('Home.Stack')
        })
        .finally(() => modelStore.set('loading', false))
      return false
    }
    return success;
  }, [autoFeederPond, autoFeederPondStore, cycleId, cycleStore, modelStore, params.auto_feeder_pond_id, params.feed_at])

  const infoComponent = () => (
    <View flexDirection="row" px="16px" py="8px">
      <JalaIcon name="info-1" size={16} color={color.palette.gray800} />
      <Text tx="autoFeederDailyFeedForm.disclaimer" preset="body3" colorScheme={colorScheme} mx="8px" />
    </View>
  )

  return <>
    <ModelCreateMultipleScreen
      colorScheme={colorScheme}
      ModelForm={ModelForm}
      initialValues={initialValues}
      modelStore={modelStore}
      Model={Feed}
      onSuccess={onSuccess}
      scrollViewRef={scrollViewRef}
      modelFormProps={{
        autoFeederPond
      }}
      infoComponent={infoComponent()}
      ignoreDirty={true}
    />
  </>
})
